import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import moment from 'moment'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import ScienceIcon from '@mui/icons-material/Science'
import API from '../../services/API'

// Public page behind the printed QR codes in COSHH stores — no login. The
// token is a long random secret; the server returns a short-lived presigned
// URL for the PDF, so a refresh mints a fresh link if it has expired.
const CoshhPublicView = () => {
  const { token } = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [sheet, setSheet] = useState(null)

  const load = () => {
    setLoading(true)
    setError(null)
    API.get(`/public/coshh/${token}`, { skipGlobalErrorHandler: true })
      .then((res) => {
        if (res.status === 200) {
          setSheet(res.data)
        } else {
          setError(
            res.data?.error ||
              'This link is not valid. It may have been removed.',
          )
        }
      })
      .catch(() =>
        setError(
          'Unable to load this data sheet. Please check your connection and try again.',
        ),
      )
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [token])

  return (
    <Box
      minHeight="100vh"
      display="grid"
      sx={{ placeItems: 'center', backgroundColor: '#F1F5F9', p: 2 }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          maxWidth: 440,
          width: '100%',
          border: '1px solid rgba(15,23,42,0.08)',
        }}
      >
        {loading ? (
          <Stack alignItems="center" spacing={2} py={4}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Loading data sheet...
            </Typography>
          </Stack>
        ) : error ? (
          <Stack alignItems="center" spacing={1.5} py={2} textAlign="center">
            <ReportProblemIcon color="warning" sx={{ fontSize: 48 }} />
            <Typography fontWeight={800}>
              COSHH data sheet unavailable
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {error}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              If this label is still in use, contact your site manager to
              reprint the QR code.
            </Typography>
            <Button variant="outlined" onClick={load}>
              Try Again
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1.5,
                  display: 'grid',
                  placeItems: 'center',
                  backgroundColor: '#242F40',
                  color: 'white',
                }}
              >
                <ScienceIcon />
              </Box>
              <Box minWidth={0}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                  textTransform="uppercase"
                >
                  COSHH Data Sheet
                </Typography>
                <Typography fontWeight={800} sx={{ lineHeight: 1.2 }}>
                  {sheet.name}
                </Typography>
              </Box>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              {[
                sheet.manufacturer,
                sheet.locationName || 'All locations',
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>

            {sheet.hazards?.length > 0 && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {sheet.hazards.map((hazard) => (
                  <Chip
                    key={hazard}
                    size="small"
                    label={hazard}
                    color="warning"
                    variant="outlined"
                    sx={{ borderRadius: 1, fontWeight: 700 }}
                  />
                ))}
              </Stack>
            )}

            {sheet.notes && (
              <Typography variant="body2">{sheet.notes}</Typography>
            )}

            {sheet.reviewDate && (
              <Typography variant="caption" color="text.secondary">
                {`Sheet review date: ${moment(sheet.reviewDate).format(
                  'Do MMM YYYY',
                )}`}
              </Typography>
            )}

            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={<OpenInNewIcon />}
              href={sheet.url}
              target="_blank"
              rel="noopener"
              sx={{ fontWeight: 800 }}
            >
              Open Data Sheet (PDF)
            </Button>
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
            >
              Provided via AmbuNet
            </Typography>
          </Stack>
        )}
      </Paper>
    </Box>
  )
}

export default CoshhPublicView
